import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { useEffect, useRef, useState } from "react";
import { config } from "./config";
import { ReviewResult } from "./ResultTypes";
import { getAllMapObjects } from "./services";
import "./styles/App.scss";
import { encode } from "./utils";

mapboxgl.accessToken = config.MAPBOX_TOKEN;

interface MapData {
  reviews: ReviewResult[];
  vacancies: any[];
  organizing: any[];
  rentAsPercentOfIncome: any[];
}

function Map() {
  const map = useRef<mapboxgl.Map | null>(null);
  const mapContainer = useRef<HTMLDivElement | null>(null);
  const markersRef = useRef<mapboxgl.Marker[]>([]);
  const [mapData, setMapData] = useState<MapData | null>(null);
  const [active, setActive] = useState(0);
  const [mapReady, setMapReady] = useState(false);

  // Initialize map
  useEffect(() => {
    if (map.current || !mapContainer.current) return;

    const mapInstance = new mapboxgl.Map({
      container: mapContainer.current,
      style: config.MAPBOX_STYLE,
      zoom: 12,
      center: config.get("coords"),
    });

    mapInstance.on("load", () => {
      setMapReady(true);
    });

    map.current = mapInstance;

    return () => {
      mapInstance.remove();
      map.current = null;
    };
  }, []);

  // Fetch all data once
  useEffect(() => {
    getAllMapObjects().then((data) => {
      setMapData({
        reviews: data.reviews ?? [],
        vacancies: data.vacancies ?? [],
        organizing: data.organizing ?? [],
        rentAsPercentOfIncome: data.income ?? [],
      });
    });
  }, []);

  // Convert income data to GeoJSON format
  const convertToGeoJSON = (data: any[]) => {
    const features = data
      .filter((item) => {
        const lng = parseFloat(item.longitude);
        const lat = parseFloat(item.latitude);
        return !isNaN(lng) && !isNaN(lat);
      })
      .map((item) => ({
        type: "Feature" as const,
        properties: {
          property_id: item.property_id,
          reported_at: item.reported_at,
          percent: parseFloat(item.percent) || 0,
          percentLabel: `${Math.round(parseFloat(item.percent) || 0)}%`,
        },
        geometry: {
          type: "Point" as const,
          coordinates: [parseFloat(item.longitude), parseFloat(item.latitude)],
        },
      }));

    return {
      type: "FeatureCollection" as const,
      features,
    };
  };

  // Update markers/heatmap when tab changes or data loads
  useEffect(() => {
    if (!map.current || !mapReady || !mapData) {
      return;
    }

    // Clear existing markers
    markersRef.current.forEach((marker) => marker.remove());
    markersRef.current = [];

    // Remove existing heatmap layers and source if they exist
    if (map.current.getLayer("income-heat")) {
      map.current.removeLayer("income-heat");
    }
    if (map.current.getLayer("income-point")) {
      map.current.removeLayer("income-point");
    }
    if (map.current.getLayer("income-label")) {
      map.current.removeLayer("income-label");
    }
    if (map.current.getSource("income-data")) {
      map.current.removeSource("income-data");
    }

    // Remove reviews heatmap layers
    if (map.current.getLayer("reviews-heat")) {
      map.current.removeLayer("reviews-heat");
    }
    if (map.current.getLayer("reviews-point")) {
      map.current.removeLayer("reviews-point");
    }
    if (map.current.getSource("reviews-data")) {
      map.current.removeSource("reviews-data");
    }

    // If "All Reviews" tab is selected, show reviews heatmap
    if (active === 0) {
      const reviewsData = mapData.reviews;

      if (!Array.isArray(reviewsData) || reviewsData.length === 0) {
        return;
      }

      const geojsonData = {
        type: "FeatureCollection" as const,
        features: reviewsData
          .filter((item) => {
            const lng = parseFloat(item.longitude);
            const lat = parseFloat(item.latitude);
            return !isNaN(lng) && !isNaN(lat);
          })
          .map((item) => ({
            type: "Feature" as const,
            properties: {
              property_id: (item as any).property_id,
            },
            geometry: {
              type: "Point" as const,
              coordinates: [
                parseFloat(item.longitude),
                parseFloat(item.latitude),
              ],
            },
          })),
      };

      // Add GeoJSON source
      map.current.addSource("reviews-data", {
        type: "geojson",
        data: geojsonData,
      });

      // Add heatmap layer
      map.current.addLayer({
        id: "reviews-heat",
        type: "heatmap",
        source: "reviews-data",
        maxzoom: 15,
        paint: {
          "heatmap-weight": 1,
          "heatmap-intensity": [
            "interpolate",
            ["linear"],
            ["zoom"],
            0,
            1,
            15,
            3,
          ],
          "heatmap-color": [
            "interpolate",
            ["linear"],
            ["heatmap-density"],
            0,
            "rgba(33,102,172,0)",
            0.2,
            "rgba(181, 207, 103, 1)",
            0.4,
            "rgba(255, 196, 0, 1)",
            0.6,
            "rgba(255, 94, 0, 1)",
            0.8,
            "rgba(255, 38, 0, 1)",
            1,
            "rgb(178,24,43)",
          ],
          "heatmap-radius": ["interpolate", ["linear"], ["zoom"], 0, 1, 3, 10],
          "heatmap-opacity": [
            "interpolate",
            ["linear"],
            ["zoom"],
            12,
            1,
            15,
            0,
          ],
        },
      });

      // Add circle layer for zoomed-in view
      map.current.addLayer({
        id: "reviews-point",
        type: "circle",
        source: "reviews-data",
        minzoom: 12,
        paint: {
          "circle-radius": ["interpolate", ["linear"], ["zoom"], 12, 3, 18, 8],
          "circle-color": "rgba(239, 138, 98, 1)",
          "circle-stroke-color": "white",
          "circle-stroke-width": 1,
          "circle-opacity": ["interpolate", ["linear"], ["zoom"], 12, 0, 13, 1],
        },
      });

      // Change cursor on hover
      map.current.on("mouseenter", "reviews-point", () => {
        if (map.current) map.current.getCanvas().style.cursor = "pointer";
      });
      map.current.on("mouseleave", "reviews-point", () => {
        if (map.current) map.current.getCanvas().style.cursor = "";
      });

      // Add click handler for circles
      map.current.on("click", "reviews-point", (e) => {
        if (e.features && e.features[0]) {
          const props = e.features[0].properties;
          if (props?.property_id) {
            window.location.href = `/property/${encode(props.property_id)}`;
          }
        }
      });

      // Fit bounds to reviews data
      const coords = geojsonData.features.map(
        (f) => f.geometry.coordinates as [number, number]
      );

      if (coords.length === 1) {
        map.current.flyTo({
          center: coords[0],
          zoom: 15,
        });
      } else if (coords.length > 1) {
        const bounds = new mapboxgl.LngLatBounds();
        coords.forEach((coord) => bounds.extend(coord));
        map.current.fitBounds(bounds, { padding: 50, maxZoom: 15 });
      }

      return;
    }

    // If "Rent as % of Income" tab is selected, show heatmap
    if (active === 3) {
      const incomeData = mapData.rentAsPercentOfIncome;

      if (!Array.isArray(incomeData) || incomeData.length === 0) {
        return;
      }

      const geojsonData = convertToGeoJSON(incomeData);

      // Add GeoJSON source
      map.current.addSource("income-data", {
        type: "geojson",
        data: geojsonData,
      });

      // Add heatmap layer
      map.current.addLayer({
        id: "income-heat",
        type: "heatmap",
        source: "income-data",
        maxzoom: 15,
        paint: {
          // Weight based on percent value (0-100)
          "heatmap-weight": [
            "interpolate",
            ["linear"],
            ["get", "percent"],
            0,
            0,
            100,
            1,
          ],
          // Increase intensity by zoom level
          "heatmap-intensity": [
            "interpolate",
            ["linear"],
            ["zoom"],
            0,
            1,
            15,
            3,
          ],
          // Color ramp from blue (low) to red (high)
          "heatmap-color": [
            "interpolate",
            ["linear"],
            ["heatmap-density"],
            0,
            "rgba(33,102,172,0)",
            0.2,
            "rgba(181, 207, 103, 1)",
            0.4,
            "rgba(255, 196, 0, 1)",
            0.6,
            "rgba(255, 94, 0, 1)",
            0.8,
            "rgba(255, 38, 0, 1)",
            1,
            "rgb(178,24,43)",
          ],
          // Adjust radius by zoom level
          "heatmap-radius": ["interpolate", ["linear"], ["zoom"], 0, 4, 15, 60],
          // Fade out heatmap at higher zoom levels
          "heatmap-opacity": [
            "interpolate",
            ["linear"],
            ["zoom"],
            12,
            1,
            15,
            0,
          ],
        },
      });

      // Add circle layer for zoomed-in view
      map.current.addLayer({
        id: "income-point",
        type: "circle",
        source: "income-data",
        minzoom: 12,
        paint: {
          // Size based on percent and zoom
          "circle-radius": [
            "interpolate",
            ["linear"],
            ["zoom"],
            12,
            ["interpolate", ["linear"], ["get", "percent"], 0, 4, 100, 10],
            18,
            ["interpolate", ["linear"], ["get", "percent"], 0, 10, 100, 30],
          ],
          // Color by percent value
          "circle-color": [
            "interpolate",
            ["linear"],
            ["get", "percent"],
            0,
            "rgba(181, 207, 103, 1)",
            25,
            "rgba(255, 196, 0, 1)",
            50,
            "rgba(255, 94, 0, 1)",
            75,
            "rgba(255, 38, 0, 1)",
            100,
            "rgb(178,24,43)",
          ],
          "circle-stroke-color": "white",
          "circle-stroke-width": 1,
          // Fade in circles as heatmap fades out
          "circle-opacity": ["interpolate", ["linear"], ["zoom"], 12, 0, 13, 1],
        },
      });

      // Add text label layer
      map.current.addLayer({
        id: "income-label",
        type: "symbol",
        source: "income-data",
        layout: {
          "text-field": [
            "concat",
            ["to-string", ["round", ["get", "percent"]]],
            "%",
          ],
          "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
          "text-size": ["interpolate", ["linear"], ["zoom"], 0, 8, 18, 14],
          "text-anchor": "center",
          "text-allow-overlap": true,
        },
        paint: {
          "text-color": "#ffffff",
          "text-halo-color": "#000000",
          "text-halo-width": 2,
        },
      });

      // Change cursor on hover
      map.current.on("mouseenter", "income-point", () => {
        if (map.current) map.current.getCanvas().style.cursor = "pointer";
      });
      map.current.on("mouseleave", "income-point", () => {
        if (map.current) map.current.getCanvas().style.cursor = "";
      });

      // Add click handler for circles
      map.current.on("click", "income-point", (e) => {
        if (e.features && e.features[0]) {
          const props = e.features[0].properties;
          if (props?.property_id) {
            window.location.href = `/property/${encode(props.property_id)}`;
          }
        }
      });

      // Fit bounds to income data
      const coords = geojsonData.features.map(
        (f) => f.geometry.coordinates as [number, number]
      );

      if (coords.length === 1) {
        map.current.flyTo({
          center: coords[0],
          zoom: 15,
        });
      } else if (coords.length > 1) {
        const bounds = new mapboxgl.LngLatBounds();
        coords.forEach((coord) => bounds.extend(coord));
        map.current.fitBounds(bounds, { padding: 50, maxZoom: 15 });
      }

      return;
    }

    // For tabs 1 and 2 (Vacancies, Organizing), use markers
    const tabConfig: { [key: number]: { data: any[]; className: string } } = {
      1: { data: mapData.vacancies, className: "circle-marker vacancy-marker" },
      2: {
        data: mapData.organizing,
        className: "circle-marker organizing-marker",
      },
    };

    const config = tabConfig[active];
    if (!config) {
      return;
    }

    const { data: dataToDisplay, className: markerClass } = config;

    if (!Array.isArray(dataToDisplay) || dataToDisplay.length === 0) {
      return;
    }

    const coords: Array<[number, number]> = [];

    for (const item of dataToDisplay) {
      const lng = parseFloat(item.longitude);
      const lat = parseFloat(item.latitude);

      // Skip invalid coordinates
      if (isNaN(lng) || isNaN(lat)) {
        continue;
      }

      const latlng: [number, number] = [lng, lat];
      coords.push(latlng);

      const markerEl = document.createElement("div");
      markerEl.className = markerClass;

      const marker = new mapboxgl.Marker(markerEl)
        .setLngLat(latlng)
        .addTo(map.current!);

      markerEl.addEventListener("click", () => {
        window.location.href = `/property/${encode(item.property_id)}`;
      });

      markersRef.current.push(marker);
    }

    // Fit bounds if we have coordinates
    if (coords.length === 1) {
      map.current.flyTo({
        center: coords[0],
        zoom: 15,
      });
    } else if (coords.length > 1) {
      const bounds = new mapboxgl.LngLatBounds();
      coords.forEach((coord) => bounds.extend(coord));
      map.current.fitBounds(bounds, { padding: 50, maxZoom: 15 });
    }
  }, [active, mapData, mapReady]);

  return (
    <div className="map-wrapper">
      <div ref={mapContainer} className="map-container-2" />
      <div className="map-tabs">
        {["All Reviews", "Vacancies", "Organizing", "Rent as % of Income"].map(
          (t, i) => (
            <button
              key={t}
              className={active === i ? "active" : ""}
              onClick={() => setActive(i)}
            >
              {t}
            </button>
          )
        )}
      </div>
    </div>
  );
}

export default Map;
